// NUDE BUT DRESSED — ranking carousel
// Tab buttons switch which collection panel is visible; a live counter tracks
// horizontal scroll position within the active panel. No dependencies.

class RankingCarousel extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.ranking-carousel__tab'));
    this.panels = Array.from(this.querySelectorAll('.ranking-carousel__panel'));
    this.counterCurrent = this.querySelector('[ref="counterCurrent"]');
    this.counterTotal = this.querySelector('[ref="counterTotal"]');
    this.activeIndex = 0;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(Number(tab.dataset.index)));
      tab.addEventListener('keydown', (e) => this.onTabKey(e));
    });

    // Track scroll on every panel so the counter is ready when switched.
    this.panels.forEach((panel) => {
      const track = panel.querySelector('.ranking-carousel__track');
      if (!track) return;
      let raf = null;
      track.addEventListener(
        'scroll',
        () => {
          if (raf) return;
          raf = requestAnimationFrame(() => {
            raf = null;
            if (Number(panel.dataset.index) === this.activeIndex) this.updateCounter();
          });
        },
        { passive: true }
      );
    });

    window.addEventListener('resize', () => this.updateCounter(), { passive: true });
    this.select(0, true);
  }

  select(index, silent) {
    if (index < 0 || index >= this.panels.length) return;
    this.activeIndex = index;

    this.tabs.forEach((tab, i) => tab.setAttribute('aria-selected', i === index ? 'true' : 'false'));

    // Keep the chosen tab visible inside the horizontal tab strip.
    // NOTE: never use scrollIntoView() here — it scrolls every scrollable
    // ancestor, including the page itself, which would jump the visitor down
    // to this section on load. We only ever move the strip horizontally.
    if (!silent) this.revealTab(this.tabs[index]);
    this.panels.forEach((panel, i) => {
      const active = i === index;
      panel.classList.toggle('is-active', active);
      if (active) panel.removeAttribute('hidden');
      else panel.setAttribute('hidden', '');
    });

    // Reset the newly shown track to its start.
    const track = this.panels[index].querySelector('.ranking-carousel__track');
    if (track && !silent) track.scrollLeft = 0;
    this.updateCounter();
  }

  // Horizontal-only reveal: adjusts the tab strip's own scrollLeft and nothing
  // else, so the page scroll position is never touched.
  revealTab(tab) {
    const strip = this.querySelector('.ranking-carousel__tabs');
    if (!strip || !tab) return;
    const stripBox = strip.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    let delta = 0;
    if (tabBox.left < stripBox.left) delta = tabBox.left - stripBox.left - 16;
    else if (tabBox.right > stripBox.right) delta = tabBox.right - stripBox.right + 16;
    if (delta === 0) return;
    strip.scrollBy({ left: delta, behavior: 'smooth' });
  }

  onTabKey(e) {
    const i = this.tabs.indexOf(e.target);
    if (i === -1) return;
    let next = null;
    if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
    if (next === null) return;
    e.preventDefault();
    this.tabs[next].focus({ preventScroll: true });
    this.select(next);
  }

  // Counter = which "page" of visible cards you're on, out of total pages.
  updateCounter() {
    if (!this.counterCurrent || !this.counterTotal) return;
    const panel = this.panels[this.activeIndex];
    const track = panel && panel.querySelector('.ranking-carousel__track');
    if (!track) return;

    const card = track.querySelector('.ranking-carousel__card, .ranking-carousel__more');
    const cardWidth = card ? card.getBoundingClientRect().width : track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
    const step = cardWidth + gap;
    const perView = Math.max(1, Math.round(track.clientWidth / step));
    const totalCards = track.children.length;

    const totalPages = Math.max(1, Math.ceil(totalCards / perView));
    const current = Math.min(totalPages, Math.floor(track.scrollLeft / (step * perView) + 0.5) + 1);

    this.counterCurrent.textContent = current;
    this.counterTotal.textContent = totalPages;
  }
}

if (!customElements.get('ranking-carousel')) {
  customElements.define('ranking-carousel', RankingCarousel);
}
